import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/provider/SubscriptionProvider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, TrendingUp, Users } from "lucide-react";

const expiredDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

const formatExpiredDate = (dateString: string | null) => {
  if (!dateString) return null;
  try {
    return expiredDateFormatter.format(new Date(dateString));
  } catch {
    return dateString;
  }
};

interface PaywallModalProps {
  isOpen: boolean;
  onDevUnlock?: () => void;
}

export function PaywallModal({ isOpen, onDevUnlock }: PaywallModalProps) {
  const navigate = useNavigate();
  const { status, expiredOn, nextBillingDate } = useSubscription();

  const isExpired = status === "suspended";
  const isNewUser = status === null;
  const expiredDate = expiredOn ?? nextBillingDate;
  const expiredDateText = formatExpiredDate(expiredDate);

  const handleManageSubscription = () => {
    navigate("/settings");
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            {isExpired
              ? "Your Subscription Has Ended"
              : isNewUser
              ? "Start Your Journey with FirstRep"
              : "Subscription Required"}
          </DialogTitle>
          <DialogDescription className="text-base">
            {isExpired
              ? "Renew your subscription to regain full access to your gym's retention dashboard and member management tools."
              : "Unlock powerful features to grow your gym and retain more members."}
          </DialogDescription>
          {isExpired && expiredDateText && (
            <p className="text-sm font-medium text-destructive">
              Expired on {expiredDateText}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid gap-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="font-medium text-sm">Track Member Retention</p>
                <p className="text-xs text-muted-foreground">
                  Monitor at-risk members before they churn
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">AI-Powered Insights</p>
                <p className="text-xs text-muted-foreground">
                  Get automated member outreach recommendations
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <p className="font-medium text-sm">Member Management</p>
                <p className="text-xs text-muted-foreground">
                  Extend memberships and track check-ins easily
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleManageSubscription}
            className="w-full"
            size="lg"
          >
            {isExpired ? "Manage Subscription" : "Get Started"}
          </Button>

          {/* Dev bypass - only in development */}
          {import.meta.env.DEV && onDevUnlock && (
            <Button
              onClick={onDevUnlock}
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground hover:text-foreground"
            >
              Dev: Unlock App
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
